/**
 * Library documents section component for citations
 */

import { useTranslation } from "react-i18next"

import type { ExtraDataItem } from "../../hooks/use-citation"
import type { CategorizedCitation } from "../../utils/citation-categorizer"
import type { Document } from "@/api/models"
import type { TFunction } from "i18next"

import { getCitationFilePath, getCitationOriginalFilePath } from "@/api/api"
import { Book } from "@/components/icons"
import { personalCitationSourceType } from "@/features/ai-explorer/utils/personal-search-utils"
import {
  findLibraryDocumentForCitation,
  resolveLibraryCitationSource,
} from "@/utils/citation-utils"

import { extractChunkTextForHighlight } from "../../hooks/use-chunk-finder"
import { CitationHoverCard } from "../citation-hover-card"
import { DocumentCitationItem } from "../document-citation-item"
import { PersonalCitationItem } from "../personal-citation-item"

type LibraryDocumentsSectionProps = {
  documents: CategorizedCitation[]
  documentsList: [string, Document][]
  citationSection: string
  getDocumentExtraData: (
    documents: [string, Document][],
    citation: string
  ) => ExtraDataItem[] | null
  formatCitationDisplay: (
    citation: string,
    librariesAndDocuments?: [string, Document][]
  ) => {
    fileNameWithPage: string
    fileNameWithoutPage: string
    pageNumber: number | null
  }
  handleDownloadClicked: (citation: string, downloadFileName?: string) => void
  setExtraData: (data: ExtraDataItem[] | null) => void
  setOpenMetadataDialog: (open: boolean) => void
  findChunkForUrlCitation: (citation: string) => { text: string[] } | null
  openPreview: (
    citation: string,
    citationSection: string,
    options?: { chunkText?: string; documents?: [string, Document][] }
  ) => void
  t: TFunction
  highlightedIndex: number | null
  messageIndex?: number
  onNavigateToInline: (index: number) => void
}

export function LibraryDocumentsSection({
  documents,
  documentsList,
  citationSection,
  getDocumentExtraData,
  formatCitationDisplay,
  handleDownloadClicked,
  setExtraData,
  setOpenMetadataDialog,
  findChunkForUrlCitation,
  openPreview,
  t,
  highlightedIndex,
  messageIndex,
  onNavigateToInline,
}: LibraryDocumentsSectionProps) {
  const { t: translate } = useTranslation()

  if (documents.length === 0) return null

  return (
    <div className="space-y-2">
      <h4 className="text-muted-foreground flex items-center gap-2 text-sm font-semibold">
        <Book className="size-4" />
        <span>
          {translate("answer.library_documents")} ({documents.length})
        </span>
      </h4>
      <div className="grid grid-cols-1 gap-2">
        {documents.map(({ citation, index }) => {
          const resolvedCitation = resolveLibraryCitationSource(citation, documentsList)
          const matchingDocument = findLibraryDocumentForCitation(resolvedCitation, documentsList)
          const previewPath = getCitationFilePath(
            resolvedCitation,
            citationSection,
            matchingDocument
          )
          const originalPath = getCitationOriginalFilePath(
            resolvedCitation,
            citationSection,
            matchingDocument
          )
          const downloadPath = originalPath ?? previewPath
          // The opaque original locator ends in "original", so the saved file
          // would be named that unless the cited file's own name is passed on.
          const downloadFileName = originalPath
            ? matchingDocument?.original_sourcefile?.trim() || undefined
            : undefined
          const extraData = getDocumentExtraData(documentsList, resolvedCitation)
          const chunk =
            findChunkForUrlCitation(citation) ?? findChunkForUrlCitation(resolvedCitation)
          const chunkText = extractChunkTextForHighlight(chunk, citation)
          const category = matchingDocument?.category
          const { fileNameWithoutPage, pageNumber } = formatCitationDisplay(
            resolvedCitation,
            documentsList
          )
          const colonIndex = fileNameWithoutPage.indexOf(": ")
          const title =
            colonIndex !== -1 ? fileNameWithoutPage.substring(colonIndex + 2) : fileNameWithoutPage

          // A citation to the user's own mail, file or chat is labelled with where it came from
          // and is never opened: there is no preview or download of a personal item after its turn.
          const personalSourceType = personalCitationSourceType(matchingDocument?.extra_data)
          if (personalSourceType) {
            return (
              <PersonalCitationItem
                key={index}
                index={index}
                isHighlighted={highlightedIndex === index}
                messageIndex={messageIndex}
                sourceType={personalSourceType}
                title={title}
                onNavigateToInline={onNavigateToInline}
              />
            )
          }

          return (
            <CitationHoverCard
              key={index}
              chunkText={chunkText}
              citation={resolvedCitation}
              citationSection={citationSection}
              documentsList={documentsList}
              pageNumber={pageNumber}
              title={title}
              onViewDocument={openPreview}
            >
              <DocumentCitationItem
                isLibraryDocument
                category={category}
                chunkText={chunkText}
                citation={resolvedCitation}
                citationSection={citationSection}
                documentsList={documentsList}
                downloadFileName={downloadFileName}
                extraData={extraData}
                formatCitationDisplay={formatCitationDisplay}
                handleDownloadClicked={handleDownloadClicked}
                index={index}
                isHighlighted={highlightedIndex === index}
                messageIndex={messageIndex}
                pathWithPage={downloadPath}
                setExtraData={setExtraData}
                setOpenMetadataDialog={setOpenMetadataDialog}
                t={t}
                onNavigateToInline={onNavigateToInline}
                onViewDocument={openPreview}
              />
            </CitationHoverCard>
          )
        })}
      </div>
    </div>
  )
}
